import { PropsWithChildren, useMemo, useState } from 'react';

import { Typography } from '@mui/material';

import { X } from '../../../icons';
import {
  List,
  ListItem,
  ListItemProps,
  ListItemText,
  ListSubheader,
} from '../../../lists';
import { tokens } from '../../../../theme/generated/tokens';
import { useDatePickerContext } from '../../components/useDatePickerContext';
import { PeriodComparisonPickerProviderProps, PeriodPreset } from '../../types';
import { periodToString } from '../../utils';

export type ValidatedPreset = PeriodPreset & { disabled?: boolean };

const PresetListItem = ({
  children,
  selected,
  ...rest
}: PropsWithChildren<ListItemProps>) => {
  return (
    <ListItem
      sx={{
        borderRadius: tokens.theme.radius.sm,
        padding: '7px 8px',
      }}
      component="li"
      endAdornment={
        selected ? (
          <X size={16} data-testid="remove-preset-button" />
        ) : undefined
      }
      selected={selected}
      {...rest}
    >
      {children}
    </ListItem>
  );
};

export const PeriodPresetsList = () => {
  const ctx = useDatePickerContext<PeriodComparisonPickerProviderProps>();
  const [isCustomModeActive, setIsCustomModeActive] = useState(false);

  const hasCustomPeriod = useMemo(() => {
    return (
      !!ctx.activePeriod &&
      !ctx.periodPresets.some((p) => p.periodInMins === ctx.activePeriod)
    );
  }, [ctx.activePeriod, ctx.periodPresets]);

  return (
    <List size="small" disablePadding testId="period-picker-presets">
      <ListSubheader
        sx={{
          alignItems: 'center',
          padding: 8,
          color: tokens.theme.fg.structural.primary,
        }}
      >
        <Typography variant="P12M">Date range:</Typography>
      </ListSubheader>

      {ctx.periodPresets?.map((preset) => {
        const isSelected = preset.periodInMins === ctx.activePeriod;
        return (
          <PresetListItem
            key={preset.title}
            selected={isSelected}
            disabled={preset.disabled}
            onClick={() => {
              if (isSelected) {
                ctx.setActivePeriod(undefined);
              } else {
                ctx.setActivePeriod(preset.periodInMins);
              }
              ctx.clearRanges();
            }}
            testId={'period-preset-' + preset.title}
          >
            <ListItemText
              primary={preset.title}
              primaryTypographyProps={{ variant: 'P12R' }}
            />
          </PresetListItem>
        );
      })}
      <PresetListItem
        selected={hasCustomPeriod || (!ctx.activePeriod && isCustomModeActive)}
        testId="custom-period-preset"
        onClick={() => {
          if (ctx.activePeriod) {
            setIsCustomModeActive(!hasCustomPeriod);
            ctx.setActivePeriod(undefined);
          } else {
            setIsCustomModeActive(!isCustomModeActive);
          }
          ctx.clearRanges();
        }}
      >
        <ListItemText
          primary={`Custom${
            hasCustomPeriod && ctx.activePeriod
              ? `: ${periodToString(ctx.activePeriod)}`
              : ''
          }`}
          primaryTypographyProps={{ variant: 'P12R' }}
        />
      </PresetListItem>
    </List>
  );
};
